import { Component, Inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  AUTHENTICATED_USER_ACTIONS,
  AuthenticatedUserActions,
  USER_QUERIES,
  UserQueries
} from '@/app/platform/platform-core/user/store/types';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { openAppQueryParam, platformBeheerAppName } from '@/app/platform/platform-core/user/constants';
import { resolveAppIcon } from '@core/components/egi-app-menu/app-icon.map';
import { AppSettingsPanelService } from '@core/services/app-settings-panel.service';

@Component({
    selector: 'authenticated-user-indicator',
    templateUrl: './authenticated-user-indicator.component.html',
    styleUrls: ['./authenticated-user-indicator.component.scss'],
    standalone: false
})
export class AuthenticatedUserIndicatorComponent {
  constructor(
    @Inject(USER_QUERIES)
    readonly userQueries: UserQueries,
    @Inject(AUTHENTICATED_USER_ACTIONS)
    readonly authenticatedUserActions: AuthenticatedUserActions,
    private readonly router: Router,
    private readonly appSettingsPanel: AppSettingsPanelService
  ) {}

  userSettingsDialogOpened = false;
  showActions = false;

  readonly displayName$ = this.userQueries.authenticatedUser$.pipe(
    map(u => `${u?.firstName} ${u?.lastName}`)
  );

  readonly platformBeheerApp$ = this.userQueries.availableApps$.pipe(
    map(apps => apps.find(a => a.isPlatformBeheer && a.rootNavigationItem.isBaseAccessAllowed))
  );

  get platformBeheerIcon(): string | undefined {
    return resolveAppIcon(platformBeheerAppName);
  }

  onLogout(): void {
    this.showActions = false;
    this.authenticatedUserActions.desktopLogout();
  }

  navigateToPlatformBeheer(app: UserEgiApp): void {
    this.showActions = false;
    this.router.navigate([app.rootNavigationItem.clone().routerLink], {
      queryParams: { navId: app.rootNavigationItem.id, [openAppQueryParam]: true }
    });
  }

  onUserIconClick() {
    this.userSettingsDialogOpened = true;
  }

  /**
   * Of er in de huidige context een applicatie-instellingenpaneel beschikbaar
   * is (de HD-app-shell meldt dit). Het menu-item verschijnt enkel dan, zodat
   * het uitsluitend binnen de betreffende app zichtbaar is.
   */
  isAppSettingsAvailable(): boolean {
    return this.appSettingsPanel.available();
  }

  /**
   * "Applicatie-instellingen": vraagt het openen van het app-brede settings-
   * paneel aan. De HD-app-shell (aanwezig op elke HD-pagina) opent het ter
   * plekke — geen navigatie meer nodig, en het item toont enkel binnen de app.
   */
  openAppSettings(): void {
    this.appSettingsPanel.requestOpen();
  }

  getInitials(user: { firstName?: string; lastName?: string; displayName?: string }): string {
    if (!user) {
      return '';
    }
    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    if (first || last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    const display = (user.displayName ?? '').trim();
    if (!display) {
      return '';
    }
    const parts = display.split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
}
